<template>
    <div v-if="jsonEditStatus" class="fixed inset-0 z-50 flex flex-col bg-black bg-opacity-75">
        <div
            class="flex items-center justify-between bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-4 shadow-lg">
            <div class="flex items-center gap-3">
                <i class="fas fa-edit text-orange-400"></i>
                <h3 class="text-lg font-bold">Form JSON Editor</h3>
            </div>
            <div class="flex gap-3">
                <!-- Apply Button -->
                <button
                    class="text-sm px-4 py-2 bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors duration-200 flex items-center gap-2 shadow-sm"
                    @click="applyJsonEdit">
                    <i class="fas fa-check"></i> Apply Changes
                </button>
                <!-- Cancel Button -->
                <button
                    class="text-sm px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2 shadow-sm"
                    @click="cancelJsonEdit">
                    <i class="fas fa-times"></i> Cancel
                </button>
            </div>
        </div>
        <div class="flex-grow overflow-auto bg-gray-900 p-6" style="min-height: 100vh;">
            <div class="flex column flex-col h-full w-full justify-between">
                <v-ace-editor
                    v-model:value="formLayoutData"
                    lang="json"
                    theme="monokai"
                    class="h-[calc(85vh)]"
                    @init="onEditorInit"
                />

            </div>
        </div>

        <!-- Error Message -->
        <div v-if="jsonEditError"
            class="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded shadow">
            {{ jsonEditError }}
        </div>

        <!-- Toast Notification -->
        <div v-if="toastMessage" class="absolute bottom-4 right-4 bg-orange-600 text-white px-4 py-2 rounded shadow">
            {{ toastMessage }}
        </div>

    </div>
</template>

<script setup>
    import { ref, watchEffect, watch, nextTick, onUnmounted, onMounted } from 'vue';

    import { VAceEditor } from 'vue3-ace-editor';
    import 'ace-builds/src-noconflict/mode-json'; // Load the language definition file used below
    import 'ace-builds/src-noconflict/theme-monokai';


    const toastMessage = ref(null);
    const jsonEditContent = ref('');
    const jsonEditError = ref('');

    let editor = null;

    const emit = defineEmits(['update:data', 'update:jsonEditStatus']);

    const props = defineProps({
        data: {
            type: Array,
            default: () => []
        },
        jsonEditStatus: {
            type: Boolean,
            default: false
        },
        updateData: {
            type: Function,
            required: false
        }
    });

    const formLayoutData = ref(JSON.stringify(props.data, null, 2));




    const onEditorInit = (editorInstance) => {
        editor = editorInstance;
    };

    const applyJsonEdit = () => {
        try {
            const parsed = JSON.parse(formLayoutData.value);
            if (!Array.isArray(parsed)) {
                throw new Error('Data must be an array');
            }
            // Call the updateData function directly if it exists
            if (props.updateData) {
                props.updateData(parsed);
            }
            emit('update:jsonEditStatus', false);
            showToast('Changes applied successfully!');
        } catch (e) {
            jsonEditError.value = e.message || 'Invalid JSON format!';
        }
    }
    const cancelJsonEdit = () => {
        if (editor) {
            editor.setValue(JSON.stringify(props.data, null, 2));
        }
        jsonEditError.value = '';
        emit('update:jsonEditStatus', false);
    }

    const showToast = (message) => {
        toastMessage.value = message;
        setTimeout(() => {
            toastMessage.value = null;
        }, 3000);
    }
</script>

<style scoped>
    .json-editor-container {
        position: relative;
        width: 100%;
        margin: 1rem 0;
    }

    .editor-wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .json-textarea {
        width: 100%;
        min-height: 300px;
        padding: 1rem;
        font-family: monospace;
        border: 1px solid #ccc;
        border-radius: 4px;
        resize: vertical;
    }

    .json-textarea.error {
        border-color: #dc3545;
    }

    .error-message {
        color: #dc3545;
        font-size: 0.875rem;
        margin-top: 0.25rem;
    }

    .button-group {
        display: flex;
        gap: 1rem;
    }

    .apply-btn,
    .cancel-btn {
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: pointer;
        border: none;
    }

    .apply-btn {
        background-color: #28a745;
        color: white;
    }

    .cancel-btn {
        background-color: #6c757d;
        color: white;
    }

    .toast-message {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        background-color: #28a745;
        color: white;
        padding: 1rem;
        border-radius: 4px;
        animation: fadeIn 0.3s ease-in-out;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(1rem);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
</style>
